import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api } from "../lib/api";
import { useI18n } from "../i18n";
import { useAuth } from "../contexts/AuthContext";
import { Sparkles, Receipt, ArrowRight, Check, X, ShoppingCart } from "lucide-react";

export default function Credits() {
    const { t } = useI18n();
    const { user, refreshUser } = useAuth();
    const [packages, setPackages] = useState([]);
    const [txs, setTxs] = useState([]);
    const [busy, setBusy] = useState(false);

    const load = async () => {
        const [pk, tx] = await Promise.all([
            api.get("/payments/packages"),
            api.get("/credits/transactions"),
        ]);
        setPackages(pk.data);
        setTxs(tx.data);
    };

    useEffect(() => { load(); }, []);

    const buy = async (pkg_id) => {
        setBusy(true);
        try {
            const r = await api.post("/payments/checkout", {
                package_id: pkg_id,
                origin_url: window.location.origin,
            });
            window.location.href = r.data.checkout_url;
        } catch (e) {
            alert(e?.response?.data?.detail || "Errore");
            setBusy(false);
        }
    };

    return (
        <div className="min-h-screen p-6 lg:p-10 max-w-5xl mx-auto">
            <header className="mb-8">
                <h1 className="font-display text-3xl font-medium tracking-tight">{t.credits.title}</h1>
                <p className="text-sm text-muted-foreground mt-1">{t.credits.equivalentEur}</p>
            </header>

            {/* Balance card */}
            <div className="rounded-xl border border-border bg-gradient-to-br from-primary/5 to-accent/10 p-8 mb-8">
                <div className="text-xs uppercase tracking-[0.1em] font-semibold text-muted-foreground mb-2">{t.credits.balance}</div>
                <div className="flex items-end gap-2">
                    <div data-testid="credits-balance" className="font-display text-5xl font-medium tracking-tight">{Number(user?.credits_balance || 0).toFixed(2)}</div>
                    <div className="text-sm text-muted-foreground mb-2">crediti</div>
                </div>
                <div className="text-sm text-muted-foreground mt-1">≈ € {Number(user?.credits_balance || 0).toFixed(2)}</div>
            </div>

            {/* Packages */}
            <section className="mb-12">
                <h2 className="font-display text-xl font-medium mb-4 flex items-center gap-2"><ShoppingCart size={16} /> {t.credits.topup}</h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                    {packages.map((p) => (
                        <button
                            key={p.id}
                            data-testid={`buy-package-${p.id}`}
                            onClick={() => buy(p.id)}
                            disabled={busy}
                            className="text-start p-5 rounded-lg border border-border bg-card hover:border-primary hover:bg-accent/40 transition-all group disabled:opacity-50"
                        >
                            <div className="text-xs uppercase tracking-[0.1em] font-semibold text-muted-foreground mb-1">{p.label.split(" — ")[0]}</div>
                            <div className="font-display text-3xl font-medium">{p.credits}</div>
                            <div className="text-xs text-muted-foreground">crediti</div>
                            <div className="mt-4 pt-4 border-t border-border flex items-center justify-between">
                                <div className="font-semibold text-sm">€ {p.eur.toFixed(2)}</div>
                                <ArrowRight size={14} className="text-primary opacity-0 group-hover:opacity-100 transition-opacity rtl-flip" />
                            </div>
                        </button>
                    ))}
                </div>
            </section>

            {/* Transactions */}
            <section>
                <h2 className="font-display text-xl font-medium mb-4 flex items-center gap-2"><Receipt size={16} /> {t.credits.history}</h2>
                <div className="rounded-lg border border-border bg-card overflow-hidden">
                    {txs.length === 0 ? (
                        <div className="p-8 text-center text-sm text-muted-foreground">{t.credits.emptyTx}</div>
                    ) : (
                        <div className="divide-y divide-border">
                            {txs.map((tx) => (
                                <div key={tx.id} data-testid={`tx-row-${tx.id}`} className="px-5 py-3 flex items-center justify-between">
                                    <div className="min-w-0">
                                        <div className="text-sm font-medium">{tx.note || tx.type}</div>
                                        <div className="text-[11px] text-muted-foreground">{new Date(tx.created_at).toLocaleString("it-IT")} · {tx.type}</div>
                                    </div>
                                    <div className={`font-mono font-semibold text-sm ${tx.delta >= 0 ? "text-orange-700" : "text-foreground"}`}>
                                        {tx.delta >= 0 ? "+" : ""}{Number(tx.delta).toFixed(2)}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </section>
        </div>
    );
}

export function PaymentSuccess() {
    const { t } = useI18n();
    const navigate = useNavigate();
    const [params] = useSearchParams();
    const { refreshUser } = useAuth();
    const [status, setStatus] = useState("checking");
    const sessionId = params.get("session_id");

    useEffect(() => {
        if (!sessionId) { setStatus("unknown"); return; }
        let cancelled = false;
        let attempts = 0;
        const poll = async () => {
            attempts++;
            try {
                const r = await api.get(`/payments/status/${sessionId}`);
                if (cancelled) return;
                if (r.data.payment_status === "paid") {
                    setStatus("paid");
                    await refreshUser();
                    return;
                }
                if (attempts > 12) {
                    setStatus("timeout");
                    return;
                }
                setTimeout(poll, 1500);
            } catch {
                if (!cancelled && attempts > 12) setStatus("timeout");
                else if (!cancelled) setTimeout(poll, 1500);
            }
        };
        poll();
        return () => { cancelled = true; };
    }, [sessionId, refreshUser]);

    return (
        <div className="min-h-screen flex items-center justify-center p-6">
            <div className="max-w-md w-full text-center">
                <div className="w-16 h-16 mx-auto rounded-full bg-orange-100 flex items-center justify-center mb-6">
                    {status === "paid" ? <Check size={28} className="text-orange-700" /> : <Sparkles size={28} className="text-orange-700 animate-pulse" />}
                </div>
                <h1 className="font-display text-2xl font-medium mb-2">{status === "paid" ? t.credits.paymentSuccess : "Verifica in corso..."}</h1>
                <p className="text-sm text-muted-foreground mb-8">{t.credits.paymentSuccessHint}</p>
                <button onClick={() => navigate("/credits")} className="px-4 py-2.5 rounded-md bg-primary text-primary-foreground hover:bg-primary/90 text-sm font-medium transition-colors">
                    {t.credits.backToCredits}
                </button>
            </div>
        </div>
    );
}

export function PaymentCancel() {
    const { t } = useI18n();
    const navigate = useNavigate();
    return (
        <div className="min-h-screen flex items-center justify-center p-6">
            <div className="max-w-md w-full text-center">
                <div className="w-16 h-16 mx-auto rounded-full bg-orange-50 flex items-center justify-center mb-6">
                    <X size={28} className="text-black" />
                </div>
                <h1 className="font-display text-2xl font-medium mb-2">{t.credits.paymentCancel}</h1>
                <p className="text-sm text-muted-foreground mb-8">{t.credits.paymentCancelHint}</p>
                <button onClick={() => navigate("/credits")} className="px-4 py-2.5 rounded-md bg-primary text-primary-foreground hover:bg-primary/90 text-sm font-medium transition-colors">
                    {t.credits.backToCredits}
                </button>
            </div>
        </div>
    );
}
